import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { LOGO, CONTACT } from "../../lib/data";

const LINKS = [
  ["About", "about"], ["Services", "services"], ["Destinations", "destinations"],
  ["Why Us", "why"], ["Process", "process"], ["FAQs", "faq"], ["Contact", "contact"],
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 border-b ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-slate-200" : "bg-transparent border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <button onClick={() => go("home")} className="flex items-center gap-2" data-testid="nav-logo">
          <img src={LOGO} alt="Global Connect immigration consultancy logo" className="h-12 w-auto object-contain" />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map(([label, id]) => (
            <button key={id} onClick={() => go(id)} data-testid={`nav-${id}-link`}
              className={`text-sm font-medium tracking-wide transition-colors ${
                scrolled ? "text-slate-700 hover:text-teal" : "text-white/90 hover:text-gold"
              }`}>
              {label}
            </button>
          ))}
          <a href={`tel:${CONTACT.phoneLink}`} data-testid="nav-call-link"
            className={`hidden xl:flex items-center gap-2 text-sm ${scrolled ? "text-slate-600" : "text-white/80"}`}>
            <Phone size={15} /> Call
          </a>
          <button onClick={() => go("consultation")} data-testid="nav-consult-btn"
            className="rounded-full bg-primary-blue text-white px-6 py-2.5 text-sm font-semibold hover:bg-teal transition-colors">
            Book Consultation
          </button>
        </div>

        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle"
          style={{ color: scrolled ? "#0f172a" : "#fff" }}>
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }} className="lg:hidden overflow-hidden bg-white border-t border-slate-200">
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map(([label, id]) => (
                <button key={id} onClick={() => go(id)} data-testid={`nav-mobile-${id}-link`}
                  className="text-left py-3 text-slate-800 font-medium border-b border-slate-100">{label}</button>
              ))}
              <button onClick={() => go("consultation")} data-testid="nav-mobile-consult-btn"
                className="mt-3 rounded-full bg-primary-blue text-white px-6 py-3 font-semibold">Book Consultation</button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
